import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BillableEdit } from 'src/app/billable/model/billableEdit';
import { BillableList } from 'src/app/billable/model/billableList';
import moment from 'moment';
import { lastValueFrom } from 'rxjs';

export interface BillableCaseItemDto
{
  id: number;
  units: number;
  manualAmount: number | null;
}

export interface BillableCaseAccumulatorRequestDto
{
  memberId: number;
  serviceDate: string; // ISO date (yyyy-MM-dd)
  excludeCaseBillableId?: number | null; // exclude current case billable being edited
  billableService: BillableCaseItemDto;
}

export interface BillableCaseAccumulatorResponseDto
{
  serviceName: string;
  hasManualAmount: boolean;
  isInformationalOnly: boolean;
  firstServiceDate: string; // ISO date (yyyy-MM-dd), or Date if preferred
  elapsedSinceStart: number | null;
  accumulatorName: string;
  frequencyName: string;
  frequencyCode: string;
  duration: number | null;
  maxUnits: number | null;
  maxAmount: number | null;
  currentUnits: number | null;
  currentAmount: number | null;
  totalUnits: number | null;
  totalAmount: number | null;
  remainingUnits: number | null;
  remainingAmount: number | null;
  validateMaximum: boolean;
  maximumExceeded: boolean;
  durationExceeded: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class BillableService
{
  constructor(private http: HttpClient) { }

  /**
   * Delete a billable service (soft).
   *
   * @param id Billable service id
   */
  public deleteBillableService(id: number): Observable<BillableList>
  {
    return this.http.delete<BillableList>(`/billable/${id}`);
  }

  /**
   * Save a billable service.
   *
   * @param data Billable service to persit
   * @param isNew Indicate if this is a new or update
   */
  public saveBillableService(data: any, isNew: boolean, billableServiceId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/billable', data);
    }
    else
    {
      return this.http.put(`/billable/${billableServiceId}`, data);
    }
  }

  public saveBillableServices(data: BillableEdit[]): Observable<any>
  {
    return this.http.put('/billable/services', data);
  }

  /**
   * Get billable service for grid list.
   */
  public getBillableServiceList(): Observable<BillableList[]>
  {
    return this.http.get<BillableList[]>('/billable/list');
  }

  /**
   * Get billable service by id.
   *
   * @param id Billable service id
   */
  public getBillableServiceById(id: number): Observable<BillableEdit>
  {
    return this.http.get<BillableEdit>(`/billable/${id}`);
  }

  /**
   * Get billable services by fee category and fee schedule.
   *
   * @param feeCategoryId Fee Category id
   * @param feeScheduleId Fee Schedule  id
   */
  public getBillableServicesByCategoryAndScheduleId(feeCategoryId: number, feeScheduleId: number): Observable<any>
  {
    return this.http.get<any>(`/billable/services?feeCategoryId=${feeCategoryId}&feeScheduleId=${feeScheduleId}`);
  }

  public getAccumulatorsByBillableServices(memberId: number, serviceDate: Date, serviceIds: [] = null): Observable<any>
  {
    const ids = serviceIds ? `&ids=${serviceIds.join()}&allowAsOfDate=false` : '&allowAsOfDate=true';
    return this.http.get(`/billable/accumulators?serviceDate=${moment(serviceDate).format('YYYY-MM-DD').toString()}&memberId=${memberId}${ids}`);
  }

  public calculateCaseBillableDiagnosesCodes(memberId: number, billableServiceId: number, serviceDate: Date): Observable<any>
  {
    return this.http.get(`/billable/calculate/diagnoses?memberId=${memberId}&serviceDate=${moment(serviceDate).format('YYYY-MM-DD').toString()}&billableServiceId=${billableServiceId}`);
  }

  public getBillableItemRate(billableServiceId: number, serviceDate: Date): Observable<any>
  {
    return this.http.get(`/billable/rate?&serviceDate=${moment(serviceDate).format('YYYY-MM-DD').toString()}&billableServiceId=${billableServiceId}`);
  }

  public checkAccumulatorsByBillableService(data: BillableCaseAccumulatorRequestDto): Observable<any>
  {
    return this.http.post('/billable/accumulators/check', data);
  }

  /**
   * Check if billable service name exists to avoid name duplicates
   *
   * @param name Billable service Name
   */
  public existsBillableServiceByName(name: string): Observable<any>
  {
    return this.http.get(`/billable/exists?name=${name}`);
  }

  public async existsBillableServiceByNameSynchronous(name: string): Promise<BillableEdit>
  {
    return lastValueFrom(this.existsBillableServiceByName(name));
  }

  /**
   * Check if billable item code exists to avoid name duplicates
   *
   * @param code Billable item Code
   */
  public billableItemByCodeExists(id: number, code: string): Observable<any>
  {
    return this.http.get(`/billable/item/${id}/exists?code=${code}`);
  }

  public async billableItemByCodeExistsSynchronous(id: number, code: string): Promise<BillableEdit>
  {
    return lastValueFrom(this.billableItemByCodeExists(id, code));
  }

  /**
   * Check if billable service is in use
   *
   * @param data Billable service Id
   */
  public billableServiceInUse(id: number): Observable<any>
  {
    return this.http.get(`/billable/inuse/${id}`);
  }

  /**
   * Get an array of Channels a billable service is using
   */
  public getChannelsPerBillableService(id: number): Observable<any[]>
  {
    return this.http.get<any>(`/billable/channels/${id}`);
  }

  /**
   * Build the accumulators data being used for the grid. Used by the select services and patient accumulator tab.
   * 
   * @param services Services loads
   * @returns Array of accumulators group by accumulator name
   */
  public getAccumulatorsData(services: any[], matchingAccumulatorName: string, isClientAccumulator: boolean): any[]
  {
    const accumulatorData = [];
    // Only rendered services with accumulators
    services.filter(x => x.accumulatorName).forEach((x: any) =>
    {
      const isPerService = x.frequencyCode === 'per_service' && !isClientAccumulator;
      const accumulatorName = isPerService ?
        accumulatorData.find((search: any) => search[matchingAccumulatorName] === `${x.accumulatorName} - ${x.name}`) :
        accumulatorData.find((search: any) => search[matchingAccumulatorName] === x[matchingAccumulatorName]);

      let currentAccumulator = accumulatorName;
      if (!accumulatorName) 
      {
        currentAccumulator = {
          "accumulatorName": isPerService ? `${x[matchingAccumulatorName]} - ${x.name}` : x[matchingAccumulatorName],
          "accumulatorNameSummary": isPerService ? `${x[matchingAccumulatorName]} - ${x.name}` : x[matchingAccumulatorName],
          "frequencyName": x.frequencyName,
          "frequencyCode": x.frequencyCode,
          "duration": x.duration,
          "maxUnits": x.maxUnits, "maxAmount": x.maxAmount,
          "totalUnits": (x.currentUnits ? x.currentUnits : 0),
          "totalAmount": (x.currentAmount ? x.currentAmount : 0),
          "remainingUnits": null, "remainingAmount": null,
          "validateMaximum": x.validateMaximum,
          "durationExceeded": x.durationExceeded,
          "firstServiceDate": x.firstServiceDate,
          "elapsedSinceStart": x.elapsedSinceStart,
          "serviceName": x.name
        };
      }

      // Set Totals
      currentAccumulator.totalUnits += (x.units ? x.units : 0);
      currentAccumulator.totalAmount += (x.totalAmount ? x.totalAmount : 0);
      currentAccumulator.durationExceeded = x.durationExceeded;
      currentAccumulator.firstServiceDate = x.firstServiceDate;
      currentAccumulator.elapsedSinceStart = x.elapsedSinceStart;

      // Set remaining
      if (x.maxUnits)
      {
        currentAccumulator.remainingUnits = currentAccumulator.maxUnits - currentAccumulator.totalUnits;
      }

      if (x.maxAmount)
      {
        currentAccumulator.remainingAmount = currentAccumulator.maxAmount - currentAccumulator.totalAmount;
      }

      if (!accumulatorName)
      {
        accumulatorData.push(currentAccumulator);
      }
    });
    return accumulatorData;
  }

  // Case Billable section

  /**
   * Create Billable for a specific case.
   * @param data Billable paylod
   */
  public createCaseBillable(data: any): Observable<any>
  {
    return this.http.post('/billable/case', data);
  }

  public addCaseBillable(caseId: number, data: any): Observable<any>
  {
    return this.http.post(`/billable/case/${caseId}`, data);
  }  

  public deleteCaseBillable(id: number): Observable<any>
  {
    return this.http.delete(`/billable/case/${id}`);
  }

  public getCaseBillable(memberId?: number, caseId?: number): Observable<any>
  {
    return this.http.get(`/billable/member/${memberId}/case/${caseId}`);
  }

  public getCaseBillableByParentId(memberId?: number, parentId?: number): Observable<any>
  {
    return this.http.get(`/billable/case/parent/${memberId}/${parentId}`);
  }

  public updateCaseBillable(data: any, caseBillableId?: number, memberId?: number): Observable<any>
  {
    return this.http.put(`/billable/case/${caseBillableId}/${memberId}`, data);
  }

  public adjustCaseBillable(data: any, memberId?: number, id?: number, adjustmentStatus?: number): Observable<any>
  {
    return this.http.post(`/billable/case/adjustment/${memberId}/${id}?adjustmentStatus=${adjustmentStatus}`, data);
  }
}
